import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UserRound, Save } from "lucide-react";
import { LogoUploader } from "@/components/settings/LogoUploader";
import { toast } from "sonner";

export function ProfileSettings() {
  const { refresh } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/profile").then((r) => setP(r.data)); }, []);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!p.name.trim()) return toast.error("Your name is required");
    setSaving(true);
    try {
      const { email, ...payload } = p;
      const { data } = await api.put("/settings/profile", payload);
      setP(data);
      await refresh();
      toast.success("Profile saved — notices will use the new details");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!p) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="profile-settings">
      <div className="flex items-start gap-3 mb-1">
        <span className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><UserRound className="h-4 w-4" /></span>
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900">Landlord profile & notice details</h2>
          <p className="text-sm text-slate-500">How you appear on notices, receipts and statements. Used by the <code className="text-[11px] bg-slate-100 px-1 rounded">{"{landlord_name}"}</code>, <code className="text-[11px] bg-slate-100 px-1 rounded">{"{landlord_phone}"}</code>, <code className="text-[11px] bg-slate-100 px-1 rounded">{"{landlord_email}"}</code> placeholders and the email footer.</p>
        </div>
      </div>
      <LogoUploader />
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Your name (signs every notice)</Label>
          <Input value={p.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Business / display name (optional)</Label>
          <Input value={p.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Southbridge Properties LLC" data-testid="profile-business" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Phone</Label>
          <Input value={p.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 010-2020" data-testid="profile-phone" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Contact email for tenant replies (blank = {p.email})</Label>
          <Input type="email" value={p.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder={p.email} data-testid="profile-contact-email" /></div>
        <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs">Mailing address (for formal notices)</Label>
          <Textarea value={p.mailing_address} onChange={(e) => set("mailing_address", e.target.value)} rows={2} placeholder="PO Box 123, Springfield, IL 62701" data-testid="profile-address" /></div>
      </div>
      <Button onClick={save} disabled={saving} className="w-full gap-2 mt-4" data-testid="profile-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save profile"}</Button>
    </div>
  );
}
